import React, { useEffect, useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Stack,
    FormControl,
    TextField,
    IconButton,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    DialogActions,
    Alert,
    Checkbox,
    Chip,
} from '@mui/material';
import { Connection } from '@domain/connection/models/Connection';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close, Info, CopyAll, CopyAllOutlined } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { container } from '@infrastructure/container';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';


interface Import4YouCredentialsFormProps {
    onNewConnection: (marketplaceConnection: Connection) => void;
    onClose: () => void;
}

const Import4YouCredentialsForm: React.FC<Import4YouCredentialsFormProps> = ({ onNewConnection, onClose }) => {
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');

    const [step, setStep] = useState(1);
    const [callbackUrlSet, setCallbackUrlSet] = useState(false);

    const [validationError, setValidationError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const callbackUrl = 'https://connectors.juststock.ai/import4you/warehouse';

    const copyCallbackUrl = () => {
        navigator.clipboard.writeText(callbackUrl);
    };

    const [secondsUntilNextSync, setSecondsUntilNextSync] = useState(0);

    useEffect(() => {
        if (step !== 3) {
            return;
        }

        const updateSecondsUntilNextSync = () => {
            const now = new Date();

            const nextSync = new Date(now);
            nextSync.setMinutes(5, 0, 0); // always XX:05:00
            if (now.getMinutes() > 5 || (now.getMinutes() === 5 && now.getSeconds() > 0)) {
                nextSync.setHours(now.getHours() + 1);
            }
            const secondsUntilNextSync = differenceInSeconds(nextSync, now);
            setSecondsUntilNextSync(secondsUntilNextSync);
        };

        // Update immediately
        updateSecondsUntilNextSync();

        // Update every second
        const interval = setInterval(updateSecondsUntilNextSync, 1000);

        return () => clearInterval(interval);
    }, [step]);

    const connectWithImport4You = async () => {
        setError(null);
        setValidationError(null);

        if (!clientId || !clientSecret || !callbackUrlSet) {
            setValidationError('Please fill in all fields');
            return;
        }

        container.import4YouConnectorRepository.addImport4YouMarketplaceConnection(clientId, clientSecret).then((response) => {
            if (response.status === 'success') {
                onNewConnection(response.data);
                setStep(3);
            } else {
                setError(response.error?.message || 'Something went wrong');
            }


        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setIsLoading(false);

        });
    };


    const handleNextStep = () => {
        if (!clientId || !clientSecret) {
            setValidationError('Please fill in all fields');
            return;
        }

        setStep(2);
    };


    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="medium">
                        Connect Import4You
                    </Typography>

                    <IconButton onClick={onClose} color="inherit">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            {step === 1 ? (
                <DialogContent>

                    <Box>
                        <Typography variant="subtitle2" color="text.primary">
                            To connect to your Import4You account, follow these steps:
                        </Typography>


                        <Typography variant="body2" color="text.secondary">
                            <ol style={{ paddingLeft: 20 }}>
                                <li>
                                    <Typography variant="body2" color="text.secondary">
                                        Go to the <a href="https://app.import4you.nl" target="_blank" rel="noopener noreferrer">Import4You</a> portal
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">
                                        In the right top corner, click on your name and navigate to Automation
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">
                                        Click on "Get credentials"
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">
                                        Fill the generated client id and client secret in the form below
                                    </Typography>
                                </li>


                            </ol>
                        </Typography>
                    </Box>

                    <FormControl fullWidth>
                        <TextField
                            label="Client ID"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            error={!!validationError}
                            helperText={validationError}
                        />
                        <TextField
                            label="Client Secret"
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={clientSecret}
                            onChange={(e) => setClientSecret(e.target.value)}
                            error={!!validationError}
                            helperText={validationError}
                        />


                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}


                    </FormControl>


                </DialogContent >

            ) : step === 2 ? (
                <DialogContent>
                    <Stack gap={1}>
                        <Typography variant="subtitle2" color="text.primary">
                            Set the callback URL
                        </Typography>



                        <Typography variant="body2" color="text.secondary">
                            In the automation settings a text field has appeared under "Warehouse update webhooks". Fill the callback URL with the following value:
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={1}>

                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                {callbackUrl}
                            </Typography>

                            <Chip icon={<CopyAllOutlined />} onClick={copyCallbackUrl} label="Copy" size="small" />
                        </Stack>

                        <Typography variant="body2" color="text.secondary">
                            Click on "Save URL"
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={1} border="1px solid" borderColor="divider" p={2} borderRadius={1} mt={1}>
                            <Checkbox
                                value="I have set the callback URL"
                                checked={callbackUrlSet}
                                onChange={(e) => setCallbackUrlSet(e.target.checked)}
                            />

                            <Stack gap={0}>
                                <Typography variant="body1" color="text.primary">
                                    I confirm that I have set the callback URL
                                </Typography>
                            </Stack>


                        </Stack>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent >

            ) : step === 3 && (
                <DialogContent>
                    <Stack gap={1}>
                        <Typography variant="subtitle2" color="text.primary">
                            You have successfully connected your Import4You account
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Import4You will automatically synchronize your inventory every hour at 5 minutes past the hour.
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Once the inventory is synced, you can finish creating the warehouse.
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={1} border="1px solid" borderColor="divider" p={2} borderRadius={1} mt={1}>
                            <Typography variant="subtitle1" color="text.primary">
                                The inventory will sync in {Math.floor(secondsUntilNextSync / 60) + ' minutes and ' + (secondsUntilNextSync % 60) + ' seconds.'}
                            </Typography>
                        </Stack>
                    </Stack>

                </DialogContent>
            )}

            < DialogActions sx={{ borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 3, justifyContent: 'flex-end' }}>

                {step === 1 ? (
                    <Button
                        variant="contained"
                        onClick={() => handleNextStep()}
                        disabled={!clientId || !clientSecret}

                    >
                        Next
                    </Button>
                ) : step === 2 ? (
                    <Stack direction="row" alignItems="center" gap={1} flex={1} justifyContent="space-between">
                        <Button variant="outlined" onClick={() => setStep(1)}>Back</Button>
                        <Button
                            variant="contained"
                            disabled={!callbackUrlSet || isLoading}
                            onClick={connectWithImport4You}
                        >
                            Connect
                        </Button>
                    </Stack>
                ) :
                    <Button
                        variant="contained"
                        onClick={onClose}
                    >
                        Finish
                    </Button>

                }

            </DialogActions >


        </>
    );
};

export default Import4YouCredentialsForm;